import { GridInputs } from "../../../../../components/Functional";
import useRequestTypes from "../../../../../hooks/useRequestTypes";

/**
 * Build the search-tab input config for Requests.
 *
 * Notes on the date fields:
 *   - "Date Start" / "Date End" filter on `request.date` — i.e. when the
 *     request was made. This used to be wired to `request.dateClosed`,
 *     which silently excluded every open/pending request (their
 *     `dateClosed` is NULL) from any bounded date search.
 *   - The "Pending" toggle directly drives `request.dateClosed` because
 *     "pending" semantically means "dateClosed IS NULL". It is bound to
 *     `_request.dateClosed.operator` with `toggleOnValue: "isNull"` and
 *     `toggleOffValue: null`, so:
 *       * toggle on  → `_request.dateClosed = { operator: "isNull" }`,
 *         which `convertToFilter` keeps (its `operator === "isNull"`
 *         short-circuit overrides the usual "drop null values" rule)
 *         and which roundtrips through the URL as
 *         `_request.dateClosed.isNull=true`;
 *       * toggle off → `operator = null`, which `convertToFilter` drops
 *         and which the URL serialiser omits.
 *     The URL is the single source of truth — there is no separate
 *     UI-only formik field — so the toggle reflects state correctly on
 *     reload and never leaks an unknown field to the server.
 *     `AdvRequestFilterInput` has no `pending` field, and including one
 *     triggers a GraphQL `VALIDATION_INVALID_TYPE_VARIABLE` error.
 *
 * The Type options are derived from the canonical `requestTypes` list
 * (loaded via GraphQL) rather than hardcoded so we don't drift from the
 * API: previously "Secondary" was hardcoded as `value: 3`, but the
 * canonical id is `2`, which made every "Secondary" search return zero
 * rows.
 */
const getRequestInputs = ({ artists, requestTypes = [] } = {}) => ({
  className: "grid grid-rows-2 gap-4",
  inputs: [
    {
      className: "grid grid-cols-1 gap-4 lg:grid-cols-4",
      inputs: [
        {
          label: "Type",
          name: "_request.typeId",
          options: requestTypes.map(type => ({
            label: type?.name,
            value: type?.id,
          })),
          type: "multi-select",
        },
        {
          label: "Artist",
          name: "_request.artistId",
          options: artists?.map(artist => ({
            label: artist?.name,
            value: artist?.id,
          })),
          type: "multi-select",
        },
        {
          label: "Date Start",
          name: "_request.date.value.from",
          type: "date",
        },
        {
          label: "Date End",
          name: "_request.date.value.to",
          type: "date",
        },
        {
          label: "Budget From",
          name: "_request.budgetFrom.value.from",
          type: "number",
        },
        {
          label: "Budget To",
          name: "_request.budgetFrom.value.to",
          type: "number",
        },
        {
          label: "Description",
          name: "_request.description",
          type: "text",
        },
        {
          label: "Notes",
          name: "_request.notes",
          type: "text",
        },
        {
          label: "Pending",
          name: "_request.dateClosed.operator",
          toggleOnValue: "isNull",
          toggleOffValue: null,
          designType: "checkbox",
          type: "toggle",
        },
      ],
    },
  ],
});

const Requests = props => {
  const { artists, formik, requestTypes: requestTypesProp } = props || {};
  const { requestTypes: fetchedRequestTypes } = useRequestTypes();
  const requestTypes = requestTypesProp ?? fetchedRequestTypes;

  const inputs = getRequestInputs({ artists, requestTypes });

  return <GridInputs formik={formik} {...inputs} />;
};

export default Requests;
export { getRequestInputs };
